import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ActionEnum,
  useActions,
  getApplicableActionsForSelection,
} from './hooks/useParticipationTableActions';
import { QuotaUpdateModal } from './components/ParticipationDetailsModal/QuotaUpdateModal';
import { RescheduleMeetingModal } from './components/ParticipationDetailsModal/RescheduleMeetingModal';
import { CancelingModal } from './components/CancelingModal';
import { UnmoderatedReportReasonModal } from './components/UnmoderatedReportReasonModal';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Button } from '@app/core';
import {
  DeclinedReason,
  Participation,
  ParticipationStatus,
  Session,
  SessionQuota,
  useHaveQuota,
  useInfiniteV2SessionParticipations,
  useSessionParticipationOnSSE,
  TabStatus,
} from '@app/api';
import { Checkbox } from 'components';
import { ParticipationDetailsModalPage } from './components/ParticipationDetailsModalPage';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import {
  FilterData,
  formatDeclinedReasonChoice,
  formatStatusChoice,
  useParticipationFilters,
} from './components/Filter';
import { useStudyTypeDetailTabs } from './hooks/participationDetailTabs';
import { useFieldConfig } from './hooks/useFieldConfig';
import {
  ParticipationTableRows,
  ParticipationTableProvider,
  ParticipationDefaultHeader,
  ParticipationDefaultLoader,
  TableSelectionActions,
} from './components/ParticipationsTable';
import { useMe } from 'queries';
import { MeetingRatingModal } from './components/ParticipationDetailsModal/MeetingRatingModal';
import { ActionBar } from 'components/ParticipationsTable';
import { useHiddenColumnsState } from 'components/ParticipationsTable/hooks/useHiddenColumnsState';
import { useParams } from 'react-router-dom';
import { useParticipationTableSelection } from './hooks/useParticipationTableSelection';

const AVG_LINE_HEIGHT = 9;

const useStyles = createUseStyles((theme: Theme) => ({
  spacer: {
    flex: 1,
  },
  whiteTableContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    borderBottomLeftRadius: theme.borderRadius.std,
    borderBottomRightRadius: theme.borderRadius.std,
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.grayBorder}`,
    minHeight: theme.marginBase * 10 * AVG_LINE_HEIGHT,
    maxHeight: theme.marginBase * 10 * AVG_LINE_HEIGHT,
    overflowX: 'auto',
    overflowY: 'auto',
  },
  tableContainer: {
    flex: 1,
    position: 'relative',
  },
  table: {
    ...theme.fonts.caption,
    width: '100%',
    borderCollapse: 'separate',
    textAlign: 'left',

    '& th, & td': {
      whiteSpace: 'nowrap',
      borderBottom: {
        width: 1,
        style: 'solid',
        color: theme.colors.grayBorder,
      },
    },
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: theme.colors.white,
      borderBottom: `1px solid ${theme.colors.grayBorder}`,
      height: theme.marginBase * 7,
    },
    '& th:first-child, & td:first-child': {
      position: 'sticky',
      left: 0,
      zIndex: 5,
    },
    '& thead th:first-child': {
      zIndex: 15,
    },
    '& th': {
      ...theme.fonts.calendarTitle,
      padding: {
        top: theme.marginBase,
        bottom: theme.marginBase,
        right: theme.marginBase * 2,
        left: theme.marginBase * 2,
      },
      color: theme.colors.inkSecondary,
      [theme.media.tablet]: {
        padding: theme.marginBase * 2,
      },
    },
    '& td': {
      whiteSpace: 'nowrap',
      padding: theme.marginBase * 2,
      height: theme.marginBase * 11,
      maxHeight: theme.marginBase * 11,
      verticalAlign: 'middle',
      [theme.media.tablet]: {
        padding: theme.marginBase * 2,
      },
    },
  },
  refreshButtonWrapper: {
    position: 'sticky',
    top: 8 * theme.marginBase,
    left: 0,
    right: 0,
    height: 0,
    zIndex: 11,
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
  },
  refreshButton: {
    display: 'flex',
    gap: theme.marginBase,
    pointerEvents: 'auto',
    boxShadow: theme.boxShadow.base,
  },
  refreshButtonIcon: {
    fontSize: theme.iconSize.regular,
  },
  selectionWrapper: {
    position: 'sticky',
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    right: 0,
    top: 0,
    bottom: theme.marginBase * 3,
    zIndex: 11,
  },
  selectAllCheckbox: {
    fontWeight: theme.fonts.caption.fontWeight,
    backgroundColor: theme.colors.white,
  },
}));

interface Props {
  session: Session;
  canSeeQuotas?: boolean;
}

export const TableView = ({ session, canSeeQuotas }: Props) => {
  const classes = useStyles();
  const { data: me } = useMe();
  const { t } = useTranslation();
  const tabs = useStudyTypeDetailTabs(session.studyType, !!canSeeQuotas);
  const params = useParams<{ tab: TabStatus }>();
  const selectedTab = params.tab as TabStatus;

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterData[]>([]);
  const [sort, setSort] = useState<
    { field: string; type: string; direction: 'asc' | 'desc' } | undefined
  >(undefined);
  const [newParticipationIds, setNewParticipationIds] = useState<string[]>([]);
  const [idsToHighlight, setIdsToHighlight] = useState<string[]>([]);
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(true);
  const [actionModal, setActionModal] = useState<{
    type?: ActionEnum;
    participation?: Participation;
  }>({});
  const [bulkAction, setBulkAction] = useState<ActionEnum>();

  const statusList = useMemo(() => {
    return tabs[selectedTab]?.statusList.map((s) => s.statuses).flat() || [];
  }, [tabs, selectedTab]);

  const openModal = useCallback(
    (
      actionType: ActionEnum,
      data: {
        participation?: Participation;
        sessionId?: string;
        participationIds?: string[];
      },
    ) => {
      if (data.participation) {
        setActionModal({ type: actionType, participation: data.participation });
        return;
      }

      if (data.sessionId && data.participationIds) {
        setBulkAction(actionType);
        return;
      }
    },
    [setActionModal, setBulkAction],
  );
  const closeModal = useCallback(() => {
    setActionModal({});
    setBulkAction(undefined);
  }, []);

  const haveDeviceQuota = useHaveQuota(
    session?.id,
    SessionQuota.ALLOWED_DEVICES_CATEGORY,
  );

  const haveDeclinedReason = statusList.includes(ParticipationStatus.DECLINED);
  const handleFilterChange = useParticipationFilters(setFilters);

  const handleSSEEvent = useCallback(
    (participation: Participation) => {
      const isBlocked = me?.id === participation?.updateCausedBy;
      if (
        participation.sessionId !== session.id ||
        !statusList.includes(participation.status) ||
        isBlocked
      ) {
        return;
      }

      setNewParticipationIds((prev) =>
        prev.includes(participation.id) ? prev : [...prev, participation.id],
      );
    },
    [me, session.id, statusList],
  );

  useSessionParticipationOnSSE(session.id, handleSSEEvent);
  const {
    data,
    fetchNextPage,
    reset,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteV2SessionParticipations(
    session.id,
    statusList,
    search,
    filters,
    sort,
  );

  const queryKey = useMemo(
    () => JSON.stringify([session.id, statusList, search, filters, sort]),
    [session.id, statusList, search, filters, sort],
  );
  const participations = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data]);
  const totalCount = data?.pages?.[0]?.pagination.count || undefined;
  const {
    selectedParticipations,
    selectedCount,
    isAllSelected,
    select,
    selectAll,
    deselectAll,
    getSelectionPayload,
    syncSelection,
    onDataChange,
  } = useParticipationTableSelection({
    sessionId: session.id,
    totalCount: totalCount || 0,
  });

  const { actionsConfig, mutationOptions } = useActions(
    session.id,
    session.studyType,
    openModal,
    deselectAll,
  );

  const handleScroll = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      if (!hasNextPage || isFetchingNextPage) return;

      const container = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = container;

      if (Math.ceil(scrollTop + clientHeight) >= scrollHeight - 50) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    setNewParticipationIds([]);
    setIdsToHighlight([]);
    setFilters([]);
    setSort(undefined);
    setSearch('');
    deselectAll();
  }, [selectedTab]);

  useEffect(() => {
    syncSelection(participations);
  }, [participations, syncSelection]);

  useEffect(() => {
    deselectAll();
    onDataChange({ status: statusList, search, filters });
  }, [filters, search, statusList]);

  const applicableBulkActions = useMemo(() => {
    return getApplicableActionsForSelection(
      selectedParticipations.map((p) => p.status),
      actionsConfig,
      session.studyType,
    );
  }, [selectedParticipations, actionsConfig, session.studyType]);

  const onClickOnRefresh = useCallback(async () => {
    setIdsToHighlight(newParticipationIds);
    setNewParticipationIds([]);
    setShouldAnimate(false);

    await reset();
    setShouldAnimate(true);
  }, [newParticipationIds, reset]);

  const fieldConfigs = useFieldConfig(
    session,
    selectedTab as TabStatus,
    actionsConfig,
  );

  const getFieldCurrentFilter = useCallback(
    (fieldKey: string): FilterData | undefined => {
      return filters.find((filter) => filter.field === fieldKey);
    },
    [filters],
  );

  const handleSortClick = useCallback(
    (fieldKey: string, fieldType: string) => {
      if (!sort || sort.field !== fieldKey) {
        setSort({ field: fieldKey, type: fieldType, direction: 'desc' });
      } else if (sort.direction === 'asc') {
        setSort({ field: fieldKey, type: fieldType, direction: 'desc' });
      } else {
        setSort({ field: fieldKey, type: fieldType, direction: 'asc' });
      }
    },
    [sort],
  );

  const statusFieldKey = !haveDeclinedReason ? 'status' : 'declinedReason';
  const statusFieldValues = useMemo(() => {
    if (!haveDeclinedReason) {
      return statusList.map((s) => formatStatusChoice(s, session.studyType));
    } else {
      return Object.values(DeclinedReason).map(formatDeclinedReasonChoice);
    }
  }, [haveDeclinedReason, session.studyType, statusList]);

  const [hiddenColumns, setHiddenColumns, setDefaultColumns] =
    useHiddenColumnsState(selectedTab as TabStatus, session);
  const showModal = (action: ActionEnum) => {
    return (
      (actionModal.type === action || bulkAction === action) &&
      (!!actionModal.participation || !!selectedParticipations?.length)
    );
  };

  const clearFilters = useCallback(() => {
    setSort(undefined);
    setSearch('');
    setFilters([]);
  }, [setFilters, setSearch, setSort]);

  return (
    <div>
      <div>
        <ActionBar
          searchResultTotal={
            filters.length > 0 || !!search ? totalCount : undefined
          }
          onSearch={setSearch}
          statusList={statusList}
          search={search}
          sessionId={session.id}
          fieldConfigs={fieldConfigs}
          hiddenColumns={hiddenColumns}
          onClearColumns={setDefaultColumns}
          onHiddenColumnsChange={setHiddenColumns}
          onClearFilters={clearFilters}
        />
        <div className={classes.whiteTableContainer} onScroll={handleScroll}>
          {newParticipationIds.length > 0 && (
            <div className={classes.refreshButtonWrapper}>
              <Button
                variant="muted"
                color="blue"
                contentClassName={classes.refreshButton}
                onClick={onClickOnRefresh}
                small
              >
                <i
                  className={classnames(
                    classes.refreshButtonIcon,
                    'ri-arrow-up-s-line',
                  )}
                ></i>
                <span>
                  {t('sessions.details.participantsDetails.newParticipants')}
                </span>
              </Button>
            </div>
          )}
          <div className={classes.tableContainer}>
            <ParticipationTableProvider
              value={{
                session,
                sessionHaveDeviceQuota: haveDeviceQuota,
                tab: selectedTab,
                sorting: { sort, handleSortClick },
                filtering: { getFieldCurrentFilter, handleFilterChange },
                special: {
                  status: {
                    fieldKey: statusFieldKey,
                    fieldValues: statusFieldValues,
                    statusList,
                    haveDeclinedReason,
                  },
                  rendering: {
                    idsToHighlight,
                  },
                },
              }}
            >
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.selectAllCheckbox}>
                      <Checkbox
                        containerClass={classes.selectAllCheckbox}
                        checked={isAllSelected}
                        indeterminate={
                          selectedParticipations.length > 0 && !isAllSelected
                        }
                        onChange={() => selectAll(participations)}
                        noMargin
                      />
                    </th>
                    {fieldConfigs.map((config) => {
                      if (
                        !config.relevant ||
                        hiddenColumns.includes(config.field)
                      ) {
                        return null;
                      }
                      if (config.renderHeader) {
                        return config.renderHeader(config);
                      }

                      return (
                        <ParticipationDefaultHeader
                          key={config.field}
                          field={config.field}
                          label={config.label}
                          sortable={config.sortable}
                          filterable={config.filterable}
                          type={config.type}
                        />
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <ParticipationTableRows
                    participations={participations}
                    selectedParticipations={selectedParticipations}
                    onSelectParticipation={select}
                    fieldConfigs={fieldConfigs}
                    shouldAnimate={shouldAnimate}
                    hiddenColumns={hiddenColumns}
                    key={queryKey}
                  />
                  {(isLoading || (isFetchingNextPage && hasNextPage)) && (
                    <tr>
                      <td></td>
                      {fieldConfigs.map((config) => {
                        if (
                          !config.relevant ||
                          hiddenColumns.includes(config.field)
                        ) {
                          return null;
                        }

                        if (config.renderLoadingCell) {
                          return config.renderLoadingCell(config);
                        }

                        return (
                          <ParticipationDefaultLoader
                            key={config.field}
                            field={config.field}
                            label={config.label}
                          />
                        );
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
            </ParticipationTableProvider>
          </div>
        </div>
        <div className={classes.selectionWrapper}>
          <TableSelectionActions
            isOpen={!!selectedParticipations?.length}
            selectedCount={selectedCount}
            totalCount={totalCount}
            selectedParticipations={selectedParticipations}
            applicableBulkActions={applicableBulkActions}
            selectionPayload={getSelectionPayload()}
          />
        </div>
        <ParticipationDetailsModalPage sessionId={session.id} />
        <QuotaUpdateModal
          isOpen={
            actionModal.type === ActionEnum.ACCEPT_WITH_QUOTA_UPDATE &&
            !!actionModal.participation
          }
          participation={actionModal.participation}
          mutationOptions={mutationOptions[ActionEnum.ACCEPT_WITH_QUOTA_UPDATE]}
          onRequestClose={closeModal}
        />
        <RescheduleMeetingModal
          isOpen={
            actionModal.type === ActionEnum.RESCHEDULE &&
            !!actionModal.participation
          }
          participation={actionModal.participation}
          mutationOptions={mutationOptions[ActionEnum.RESCHEDULE]}
          onRequestClose={closeModal}
        />
        <CancelingModal
          isOpen={
            actionModal.type === ActionEnum.CANCEL &&
            !!actionModal.participation
          }
          participation={actionModal.participation}
          mutationOptions={mutationOptions[ActionEnum.CANCEL]}
          onRequestClose={closeModal}
        />
        <MeetingRatingModal
          isOpen={
            actionModal.type === ActionEnum.RATE && !!actionModal.participation
          }
          participation={actionModal.participation}
          onRequestClose={closeModal}
          reportMutationOptions={mutationOptions[ActionEnum.REPORT]}
          payMutationOptions={mutationOptions[ActionEnum.VALIDATE]}
          onRescheduleClick={() => {
            setActionModal({
              type: ActionEnum.RESCHEDULE,
              participation: actionModal.participation,
            });
          }}
        />
        <UnmoderatedReportReasonModal
          isOpen={showModal(ActionEnum.REPORT)}
          participation={actionModal.participation}
          selectionPayload={bulkAction ? getSelectionPayload() : undefined}
          onRequestClose={closeModal}
          sessionId={session.id}
          onSuccess={() => {
            if (bulkAction) deselectAll();
          }}
        />
      </div>
    </div>
  );
};
